import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AiJobMatchCard from '../components/AiJobMatchCard'
import JobShareButton from '../components/JobShareButton'
import { getAPIBaseURL } from '../services/helpers'
import { getData, postData } from '../services/request'
import { statusMeta as baseStatusMeta } from './JobApplicationsPage'
import { getMissingMandatoryDocuments } from './ApplicationDocumentUpload'
import { getProfileStrengthInfo } from './employmeProfileUtils'
import { employmentTypeLabel, locationTypeLabel } from '../utils/employmeLabels'

const slideStyle = (delay = 0) => ({
  animationDelay: `${delay}ms`,
  animationFillMode: 'forwards',
})

const StatCard = ({ label, shortLabel, value, loading, icon, accent, delay = 0 }) => (
  <div
    className="animate-slide-up opacity-0 overflow-hidden rounded-xl border border-slate-200 bg-white px-2.5 py-3 shadow-sm sm:rounded-2xl sm:p-5 md:p-6"
    style={slideStyle(delay)}
  >
    <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:gap-4 sm:text-left">
      <div className={`mb-1.5 hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:mb-0 sm:flex sm:h-12 sm:w-12 ${accent}`}>
        {icon}
      </div>
      <div className="min-w-0 w-full">
        <p className="text-[10px] font-medium leading-tight text-slate-500 sm:hidden">{shortLabel}</p>
        <p className="hidden text-xs font-medium text-slate-500 sm:block sm:text-sm">{label}</p>
        <p className="mt-0.5 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl md:text-3xl">
          {loading ? (
            <span className="inline-block h-6 w-8 animate-pulse-soft rounded-md bg-slate-100 sm:h-8 sm:w-10 sm:rounded-lg" />
          ) : (
            value
          )}
        </p>
      </div>
    </div>
  </div>
)

const SectionCard = ({ children, className = '', delay = 0 }) => (
  <div
    className={`animate-slide-up opacity-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm shadow-slate-200/40 ${className}`}
    style={slideStyle(delay)}
  >
    {children}
  </div>
)

const ProfileStrengthPanel = ({ compact = false, profileInfo }) => (
  compact ? (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">Profile strength</p>
        <span className="text-sm font-bold text-primary-600">{profileInfo.percentage}%</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600"
          style={{
            width: `${profileInfo.percentage}%`,
            animation: 'progress-fill 1s ease-out forwards',
            '--progress-width': `${profileInfo.percentage}%`,
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[11px] text-slate-500">{profileInfo.label}</p>
        <Link
          to="/jobs/profile"
          className="inline-flex min-h-[32px] shrink-0 items-center text-[11px] font-semibold text-primary-600"
        >
          Update
        </Link>
      </div>
    </div>
  ) : (
    <div className="p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">Profile strength</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            A complete profile helps employers evaluate you faster.
          </p>
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">{profileInfo.label}</span>
          <span className="text-sm font-bold text-primary-600">{profileInfo.percentage}%</span>
        </div>
        <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600 transition-all duration-700 ease-out"
            style={{
              width: `${profileInfo.percentage}%`,
              animation: 'progress-fill 1s ease-out forwards',
              '--progress-width': `${profileInfo.percentage}%`,
            }}
          />
        </div>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">{profileInfo.helper}</p>
        <Link
          to="/jobs/profile"
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-700 shadow-sm transition-all hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Update profile
        </Link>
      </div>
    </div>
  )
)

const SmartMatchesPanel = ({ compact = false, aiLoading, aiError, aiMatches, fetchAiMatches }) => (
  compact ? (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
      <p className="text-sm font-semibold text-slate-900">Smart matches</p>
      <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
        Roles matched to your profile.
      </p>
      <button
        type="button"
        onClick={fetchAiMatches}
        disabled={aiLoading}
        className="mt-2.5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 disabled:opacity-60"
      >
        {aiLoading ? 'Finding matches…' : 'Get matches'}
      </button>
      {aiError && (
        <p className="mt-2 text-[11px] text-amber-700">{aiError}</p>
      )}
      {aiMatches.length > 0 && (
        <p className="mt-2 text-[11px] font-medium text-sky-800">
          {aiMatches.length} match{aiMatches.length === 1 ? '' : 'es'} below
        </p>
      )}
    </div>
  ) : (
    <div className="p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-primary-500">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">Smart job matches</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
            Get personalised role suggestions based on your profile. You always choose where to apply.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={fetchAiMatches}
        disabled={aiLoading}
        className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-600 disabled:opacity-60"
      >
        {aiLoading ? (
          <>
            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Finding matches…
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Get matches
          </>
        )}
      </button>

      {aiError && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {aiError}
        </div>
      )}
      {!aiLoading && !aiError && aiMatches.length === 0 && (
        <p className="mt-3 text-xs text-slate-500">
          No suggestions yet. Complete your profile, then try again.
        </p>
      )}
      {aiMatches.length > 0 && (
        <p className="mt-3 text-xs font-medium text-sky-800">
          {aiMatches.length} match{aiMatches.length === 1 ? '' : 'es'} found — scroll down to view and apply.
        </p>
      )}
    </div>
  )
)

const JobSeekerDashboardPage = () => {
  const [applications, setApplications] = useState([])
  const [appsLoading, setAppsLoading] = useState(true)
  const [appsError, setAppsError] = useState('')

  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobsError, setJobsError] = useState('')
  const [profile, setProfile] = useState(null)
  const [showCongrats, setShowCongrats] = useState(false)
  const [selectedJobTitle, setSelectedJobTitle] = useState('')
  const [aiMatches, setAiMatches] = useState([])
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState('')

  useEffect(() => {
    const fetchApplications = async () => {
      setAppsLoading(true)
      setAppsError('')
      const url = `${getAPIBaseURL()}/employme/jobseeker/applications`
      const { error: reqError, result } = await getData(url, {}, {})
      setAppsLoading(false)
      if (reqError || !result) {
        setAppsError('Unable to load applications')
        return
      }
      setApplications(result.data.data || [])
    }

    fetchApplications()
  }, [])

  const fetchAiMatches = async () => {
    setAiError('')
    setAiLoading(true)
    setAiMatches([])
    const url = `${getAPIBaseURL()}/employme/jobseeker/ai/job-matches`
    const { error: reqError, result } = await postData(
      url,
      { 'Content-Type': 'application/json' },
      JSON.stringify({})
    )
    setAiLoading(false)
    if (reqError || !result) {
      setAiError(result?.data?.data || 'Unable to load AI job matches right now.')
      return
    }
    if (result.status === 200) {
      const recs = result.data?.data?.recommendations || []
      setAiMatches(recs)
      if (!recs.length) {
        setAiError('No strong matches found yet. Try updating your profile or checking back later.')
      }
    } else {
      setAiError(result.data?.data || 'Unable to load AI job matches right now.')
    }
  }

  useEffect(() => {
    if (!applications || applications.length === 0) return

    const normalizedSelected = (status) =>
      (status || '').toString().toUpperCase().replace(/\s+/g, '_')

    const selectedApplication = applications.find(
      (app) => normalizedSelected(app.status) === 'SELECTED'
    )

    if (!selectedApplication) return

    if (typeof window !== 'undefined') {
      const storageKey = 'employme-jobseeker-selected-congrats-shown'
      const hasSeen = window.sessionStorage.getItem(storageKey)
      if (!hasSeen) {
        setSelectedJobTitle(
          selectedApplication.jobId?.title || 'a new role'
        )
        setShowCongrats(true)
        window.sessionStorage.setItem(storageKey, '1')
      }
    }
  }, [applications])

  useEffect(() => {
    const fetchJobs = async () => {
      setJobsLoading(true)
      setJobsError('')
      const url = `${getAPIBaseURL()}/employme/jobs`
      const { error: reqError, result } = await getData(url, {}, {})
      setJobsLoading(false)
      if (reqError || !result) {
        setJobsError('Unable to load jobs')
        return
      }
      setJobs(result.data.data || [])
    }

    fetchJobs()
  }, [])

  useEffect(() => {
    const fetchProfile = async () => {
      const url = `${getAPIBaseURL()}/employme/jobseeker/profile`
      const { error: reqError, result } = await getData(url, {}, {})
      if (reqError || !result) {
        return
      }
      if (result.status === 200) {
        setProfile(result.data?.data || null)
      }
    }

    fetchProfile()
  }, [])

  const statusMeta = (rawStatus) => baseStatusMeta(rawStatus)

  const applicationStats = useMemo(() => {
    const counts = {
      total: applications.length,
      interviewInProgress: 0,
      awaitingDecision: 0,
    }

    applications.forEach((app) => {
      const normalized = (app.status || '').toString().toUpperCase().replace(/\s+/g, '_')
      switch (normalized) {
        case 'INTERVIEW_IN_PROGRESS':
          counts.interviewInProgress += 1
          break
        case 'SUBMITTED':
        case 'UNDER_REVIEW':
          counts.awaitingDecision += 1
          break
        default:
          break
      }
    })

    return counts
  }, [applications])

  const profileInfo = useMemo(() => {
    if (!profile) {
      return {
        percentage: 0,
        label: 'Incomplete',
        helper: 'Create an account or log in to build your profile.',
      }
    }
    return getProfileStrengthInfo(profile)
  }, [profile])

  const recentJobs = useMemo(() => {
    if (!jobs || jobs.length === 0) return []
    return jobs.slice(0, 4)
  }, [jobs])

  const getInterviewSummary = (app) => {
    const status = (app.status || '').toString().toUpperCase().replace(/\s+/g, '_')
    if (status === 'INTERVIEW_IN_PROGRESS') return 'Interview in progress'
    if (status === 'SUBMITTED') return 'Interview submitted'
    if (status === 'UNDER_REVIEW') return 'Interview under review'
    if (status === 'SELECTED' || status === 'REJECTED' || status === 'EXPIRED') return 'Interview completed'
    return 'Interview not started yet'
  }

  const latestApplications = useMemo(() => {
    if (!applications || applications.length === 0) return []
    return applications.slice(0, 3)
  }, [applications])

  const displayName = profile?.fullName?.trim().split(/\s+/)[0] || null

  return (
    <div className="space-y-4 animate-fade-in sm:space-y-6">
      {showCongrats && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] backdrop-blur-sm animate-fade-in sm:items-center">
          <div
            className="relative w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl animate-scale-in sm:p-8"
            role="dialog"
            aria-labelledby="congrats-title"
          >
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 id="congrats-title" className="text-xl font-bold tracking-tight text-slate-900">
              Congratulations!
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              You have been <span className="font-semibold text-emerald-700">selected</span> for{' '}
              {selectedJobTitle}. An employer will reach out to you with next steps.
            </p>
            <button
              type="button"
              onClick={() => setShowCongrats(false)}
              className="mt-6 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              Continue to dashboard
            </button>
          </div>
        </div>
      )}

      {/* Welcome hero */}
      <div
        className="animate-slide-up opacity-0 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 shadow-lg shadow-slate-900/20 sm:p-6 md:p-8"
        style={slideStyle(0)}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-primary-300 sm:text-xs">
              Job seeker dashboard
            </p>
            <h1 className="mt-1 text-xl font-bold tracking-tight text-white sm:text-2xl md:text-3xl">
              {displayName ? `Welcome back, ${displayName}` : 'Welcome back'}
            </h1>
            <p className="mt-1.5 hidden max-w-lg text-sm leading-relaxed text-slate-300 sm:block">
              Track your applications, monitor interview progress, and discover roles that fit your experience.
            </p>
            <p className="mt-1.5 text-sm leading-snug text-slate-300 sm:hidden">
              Track applications and find roles that fit.
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:shrink-0 sm:flex-wrap sm:gap-3">
            <Link
              to="/jobs/browse"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-slate-900 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md"
            >
              <svg className="h-4 w-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Browse jobs
            </Link>
            <Link
              to="/jobs/applications"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition-all hover:bg-white/20"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              My applications
            </Link>
          </div>
        </div>
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatCard
          label="Total applications"
          shortLabel="Applied"
          value={applicationStats.total}
          loading={appsLoading}
          delay={80}
          accent="bg-primary-50 text-primary-600"
          icon={
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
        />
        <StatCard
          label="Interviews in progress"
          shortLabel="Interview"
          value={applicationStats.interviewInProgress}
          loading={appsLoading}
          delay={140}
          accent="bg-amber-50 text-amber-600"
          icon={
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          label="Awaiting decision"
          shortLabel="Pending"
          value={applicationStats.awaitingDecision}
          loading={appsLoading}
          delay={200}
          accent="bg-sky-50 text-sky-700"
          icon={
            <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      <div className="grid gap-3 lg:hidden">
        <div className="animate-slide-up opacity-0" style={slideStyle(220)}>
          <ProfileStrengthPanel compact profileInfo={profileInfo} />
        </div>
        <div className="animate-slide-up opacity-0" style={slideStyle(240)}>
          <SmartMatchesPanel
            compact
            aiLoading={aiLoading}
            aiError={aiError}
            aiMatches={aiMatches}
            fetchAiMatches={fetchAiMatches}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5 sm:space-y-6">
          {/* Applications */}
          <SectionCard delay={260}>
            <div className="border-b border-slate-100 px-4 py-3.5 sm:px-6 sm:py-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900">Your applications</h2>
                  <p className="mt-0.5 hidden text-sm text-slate-500 sm:block">
                    Track each step of your active hiring processes.
                  </p>
                </div>
                <Link
                  to="/jobs/applications"
                  className="inline-flex min-h-[40px] items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700 sm:min-h-0"
                >
                  View all
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            <div className="px-4 py-3.5 sm:px-6 sm:py-5">
              {appsError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {appsError}
                </div>
              )}
              {appsLoading && !appsError && (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="animate-pulse-soft rounded-xl border border-slate-100 bg-slate-50 p-4">
                      <div className="h-4 w-2/5 rounded bg-slate-200" />
                      <div className="mt-2 h-3 w-1/4 rounded bg-slate-100" />
                    </div>
                  ))}
                </div>
              )}
              {!appsLoading && !appsError && latestApplications.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-5 py-8 text-center">
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-slate-700">No applications yet</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Browse open roles below to start your first application.
                  </p>
                  <Link
                    to="/jobs/browse"
                    className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-primary-600"
                  >
                    Browse jobs
                  </Link>
                </div>
              )}

              <div className="space-y-3">
                {latestApplications.map((app, index) => {
                  const meta = statusMeta(app.status)
                  return (
                    <div
                      key={app._id}
                      className="group rounded-xl border border-slate-100 bg-slate-50/40 p-4 transition-all duration-200 hover:border-primary-100 hover:bg-white hover:shadow-sm"
                      style={slideStyle(320 + index * 60)}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-2">
                          <div className="text-sm font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">
                            {app.jobId?.title || 'Job'}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.color}`}
                            >
                              {meta.label}
                            </span>
                            <span className="text-xs text-slate-500">
                              {getInterviewSummary(app)}
                            </span>
                          </div>
                          {app.resultMessage && (
                            <div className="rounded-lg bg-white p-2.5 text-xs leading-relaxed text-slate-600 whitespace-pre-line border border-slate-100">
                              {app.resultMessage.length > 200
                                ? `${app.resultMessage.slice(0, 200).trim()}…`
                                : app.resultMessage}
                            </div>
                          )}
                        </div>
                        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-col sm:items-end">
                          <Link
                            to={`/jobs/${app.jobId?._id}`}
                            className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-primary-600 transition-colors hover:border-primary-200 hover:bg-primary-50 sm:min-h-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:justify-end"
                          >
                            View job
                          </Link>
                          {(app.status === 'ELIGIBILITY_CONFIRMED' || app.status === 'INTERVIEW_IN_PROGRESS') &&
                            (getMissingMandatoryDocuments(app.jobId, app).length > 0 ? (
                              <span className="text-xs text-amber-800">
                                Upload required documents on the job page
                              </span>
                            ) : (
                              <Link
                                to={`/jobs/applications/${app._id}/interview`}
                                className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                              >
                                {app.status === 'INTERVIEW_IN_PROGRESS' ? 'Continue interview' : 'Start interview'}
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            ))}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </SectionCard>

          {aiMatches.length > 0 && (
            <SectionCard delay={320}>
              <div className="border-b border-slate-100 px-4 py-3.5 sm:px-6 sm:py-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900">Your matches</h2>
                    <p className="mt-0.5 text-sm text-slate-500">
                      Roles matched to your profile. Click any job to view the full description and apply.
                    </p>
                  </div>
                  <span className="inline-flex w-fit items-center rounded-md bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800">
                    {aiMatches.length} match{aiMatches.length === 1 ? '' : 'es'}
                  </span>
                </div>
              </div>
              <div className="px-4 py-3.5 sm:px-6 sm:py-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  {aiMatches.map((item) => (
                    <AiJobMatchCard key={item.job?._id || item.jobId} item={item} />
                  ))}
                </div>
              </div>
            </SectionCard>
          )}

          {/* Latest jobs */}
          <SectionCard delay={380}>
            <div className="border-b border-slate-100 px-4 py-3.5 sm:px-6 sm:py-5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900">Latest opportunities</h2>
                  <p className="mt-0.5 hidden text-sm text-slate-500 sm:block">
                    Recently posted roles across a range of skills and experience levels.
                  </p>
                </div>
                <Link
                  to="/jobs/browse"
                  className="inline-flex min-h-[40px] items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700 sm:min-h-0"
                >
                  View all
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            <div className="px-4 py-3.5 sm:px-6 sm:py-5">
              {jobsError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {jobsError}
                </div>
              )}
              {jobsLoading && !jobsError && (
                <div className="space-y-3">
                  {[0, 1].map((i) => (
                    <div key={i} className="animate-pulse-soft rounded-xl border border-slate-100 p-4">
                      <div className="h-4 w-1/2 rounded bg-slate-200" />
                      <div className="mt-2 h-3 w-full rounded bg-slate-100" />
                      <div className="mt-1 h-3 w-3/4 rounded bg-slate-100" />
                    </div>
                  ))}
                </div>
              )}
              {!jobsLoading && !jobsError && recentJobs.length === 0 && (
                <p className="text-sm text-slate-500">
                  No jobs are live yet. Check back soon or refresh this page.
                </p>
              )}

              <div className="space-y-3">
                {recentJobs.map((job, index) => (
                  <div
                    key={job._id}
                    className="group rounded-xl border border-slate-100 bg-white p-4 transition-all duration-200 hover:border-primary-100 hover:shadow-md"
                    style={slideStyle(440 + index * 50)}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <Link
                          to={`/jobs/${job._id}`}
                          className="text-sm font-semibold text-slate-900 transition-colors group-hover:text-primary-700 sm:text-base"
                        >
                          {job.title}
                        </Link>
                        <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                          <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {job.location?.city}, {job.location?.state} · {locationTypeLabel(job.locationType)}
                        </p>
                      </div>
                      <span className="inline-flex w-fit shrink-0 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700">
                        {employmentTypeLabel(job.employmentType)}
                      </span>
                    </div>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600 line-clamp-2">
                      {job.description}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                      <Link
                        to={`/jobs/${job._id}`}
                        className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-primary-50 px-3 py-2 text-xs font-medium text-primary-700 hover:bg-primary-100 sm:min-h-0 sm:bg-transparent sm:px-0 sm:py-0 sm:text-primary-600 sm:hover:bg-transparent sm:hover:text-primary-700"
                      >
                        View job
                      </Link>
                      <JobShareButton
                        jobId={job._id}
                        className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 sm:min-h-0 sm:py-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Sidebar */}
        <div className="hidden space-y-5 sm:space-y-6 lg:block">
          <SectionCard delay={300}>
            <ProfileStrengthPanel profileInfo={profileInfo} />
          </SectionCard>

          <SectionCard delay={360}>
            <SmartMatchesPanel
              aiLoading={aiLoading}
              aiError={aiError}
              aiMatches={aiMatches}
              fetchAiMatches={fetchAiMatches}
            />
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

export default JobSeekerDashboardPage
